// Repositories
import { AxiosAdvertRepository } from '../repositories/AxiosAdvertRepository';
import { ChannelsAxiosRepository } from '../repositories/ChannelsAxiosRepository';
import { ChannelCategoriesAxiosRepository } from '../repositories/ChannelCategoriesAxiosRepository';
import { AdvertChannelsAxiosRepository } from '../repositories/AdvertChannelsAxiosRepository';
import { TelegramAccountsAxiosRepository } from '../repositories/TelegramAccountsAxiosRepository';
import { SendingSchedulesAxiosRepository } from '../repositories/SendingSchedulesAxiosRepository';
import { ScheduledDeliveriesAxiosRepository } from '../repositories/ScheduledDeliveriesAxiosRepository';
import { LeadsAxiosRepository } from '../repositories/LeadsAxiosRepository';
import { CurrentUserAxiosRepository } from '../repositories/CurrentUserAxiosRepository';
import { AdminChannelsAxiosRepository } from '../repositories/AdminChannelsAxiosRepository';
import { ChannelImportsAxiosRepository } from '../repositories/ChannelImportsAxiosRepository';
import { DeliveryDashboardAxiosRepository } from '../repositories/DeliveryDashboardAxiosRepository';
import { CrawlersAxiosRepository } from '../repositories/CrawlersAxiosRepository';
import { TelegramMessengerAxiosRepository } from '../repositories/TelegramMessengerAxiosRepository';
import { DiscoveredChannelsAxiosRepository } from '../repositories/DiscoveredChannelsAxiosRepository';

// Auth
import { FirebaseAuthTokenProvider } from '../auth/FirebaseAuthTokenProvider';

// Advert Use Cases
import { CreateAdvertUseCase } from '../../application/use-cases/CreateAdvert';
import { GetAdvertUseCase } from '../../application/use-cases/GetAdvert';
import { ListAdvertsUseCase } from '../../application/use-cases/ListAdverts';
import { UpdateAdvertUseCase } from '../../application/use-cases/UpdateAdvert';
import { DeleteAdvertUseCase } from '../../application/use-cases/DeleteAdvert';

// Channel Use Cases
import { CreateChannelUseCase } from '../../application/use-cases/channels/CreateChannelUseCase';
import { DeleteChannelUseCase } from '../../application/use-cases/channels/DeleteChannelUseCase';
import { GetChannelUseCase } from '../../application/use-cases/channels/GetChannelUseCase';
import { ListAllChannelsUseCase } from '../../application/use-cases/channels/ListAllChannelsUseCase';
import { ListChannelsUseCase } from '../../application/use-cases/channels/ListChannelsUseCase';
import { CountChannelsUseCase } from '../../application/use-cases/channels/CountChannelsUseCase';
import { ListChannelCategoriesUseCase } from '../../application/use-cases/channels/ListChannelCategoriesUseCase';
import { UpdateChannelUseCase } from '../../application/use-cases/channels/UpdateChannelUseCase';

// Advert-Channels Use Cases
import { GetAdvertChannelsUseCase } from '../../application/use-cases/advertChannels/GetAdvertChannelsUseCase';
import { SetAdvertChannelsUseCase } from '../../application/use-cases/advertChannels/SetAdvertChannelsUseCase';
import { GetAdvertTelegramAccountsUseCase } from '../../application/use-cases/advertChannels/GetAdvertTelegramAccountsUseCase';

// Telegram Use Cases
import { CreateTelegramAccountUseCase } from '../../application/use-cases/telegram/CreateTelegramAccountUseCase';
import { DeleteTelegramAccountUseCase } from '../../application/use-cases/telegram/DeleteTelegramAccountUseCase';
import { FinishTelegramAccountAuthUseCase } from '../../application/use-cases/telegram/FinishTelegramAccountAuthUseCase';
import { GetTelegramAccountUseCase } from '../../application/use-cases/telegram/GetTelegramAccountUseCase';
import { ListTelegramAccountsUseCase } from '../../application/use-cases/telegram/ListTelegramAccountsUseCase';
import { StartTelegramAccountAuthUseCase } from '../../application/use-cases/telegram/StartTelegramAccountAuthUseCase';
import { UpdateTelegramAccountUseCase } from '../../application/use-cases/telegram/UpdateTelegramAccountUseCase';
import { GetTelegramAccountBrandingUseCase } from '../../application/use-cases/telegram/GetTelegramAccountBrandingUseCase';
import { UpdateTelegramAccountBrandingUseCase } from '../../application/use-cases/telegram/UpdateTelegramAccountBrandingUseCase';
import { ListLeadsUseCase } from '../../application/use-cases/leads/ListLeadsUseCase';
import { ProcessLeadUseCase } from '../../application/use-cases/leads/ProcessLeadUseCase';

// User Use Cases
import { GetCurrentUserUseCase } from '../../application/use-cases/users/GetCurrentUserUseCase';

// Admin Use Cases
import {
  ListAdminChannelsUseCase,
  CreateAdminChannelUseCase,
  UpdateAdminChannelUseCase,
  CreateChannelCategoryUseCase,
  RenameChannelCategoryUseCase,
} from '../../application/use-cases/admin/AdminChannelUseCases';
import {
  StartChannelImportUseCase,
  GetChannelImportUseCase,
  ListChannelImportsUseCase,
  SaveChannelImportRowsUseCase,
  ApplyChannelImportUseCase,
} from '../../application/use-cases/admin/ChannelImportUseCases';
import {
  LoadDeliveryOverviewUseCase,
  ListDeliveryEventsUseCase,
  GetDeliveryEventUseCase,
} from '../../application/use-cases/admin/DeliveryDashboardUseCases';
import {
  ListCrawlersUseCase,
  AddCrawlerUseCase,
  ImportCrawlerUseCase,
  ListCrawlerChannelsUseCase,
  AssignCrawlerChannelsUseCase,
  UnassignCrawlerChannelUseCase,
  RequestCrawlerCodeUseCase,
  ConfirmCrawlerCodeUseCase,
  ReleaseCrawlerUseCase,
} from '../../application/use-cases/admin/CrawlerUseCases';
import {
  ListTelegramDialogsUseCase,
  ListTelegramDialogMessagesUseCase,
  SendTelegramMessageUseCase,
  GetTelegramInboxSettingsUseCase,
  UpdateTelegramInboxSettingsUseCase,
  SearchTelegramPeersUseCase,
  JoinTelegramPeerUseCase,
  PressTelegramInlineButtonUseCase,
} from '../../application/use-cases/admin/MessengerUseCases';
import {
  ListDiscoveredChannelsUseCase,
  ImportDiscoveredChannelsUseCase,
  RejectDiscoveredChannelsUseCase,
} from '../../application/use-cases/admin/DiscoveredChannelUseCases';

// Sending Schedule Use Cases
import { CreateSendingScheduleUseCase } from '../../application/use-cases/sending-schedules/CreateSendingScheduleUseCase';
import { ListSendingSchedulesUseCase } from '../../application/use-cases/sending-schedules/ListSendingSchedulesUseCase';
import { GetSendingScheduleUseCase } from '../../application/use-cases/sending-schedules/GetSendingScheduleUseCase';

// Scheduled Deliveries Use Cases
import { GetScheduledDeliveryUseCase } from '../../application/use-cases/scheduled-deliveries/GetScheduledDeliveryUseCase';
import { ListScheduledDeliveriesUseCase } from '../../application/use-cases/scheduled-deliveries/ListScheduledDeliveriesUseCase';

// Repository Interfaces
import type { IAdvertRepository } from '../../application/ports/IAdvertRepository';
import type { IChannelRepository } from '../../application/ports/IChannelRepository';
import type { IChannelCategoryRepository } from '../../application/ports/IChannelCategoryRepository';
import type { IAdvertChannelRepository } from '../../application/ports/IAdvertChannelRepository';
import type { ITelegramAccountRepository } from '../../application/ports/ITelegramAccountRepository';
import type { ISendingScheduleRepository } from '../../application/ports/ISendingScheduleRepository';
import type { IScheduledDeliveryRepository } from '../../application/ports/IScheduledDeliveryRepository';
import type { ILeadRepository } from '../../application/ports/ILeadRepository';

// Factory interfaces for better type safety
interface RepositoryFactory {
  createAdvertRepository(): IAdvertRepository;
  createChannelRepository(): IChannelRepository;
  createChannelCategoryRepository(): IChannelCategoryRepository;
  createAdvertChannelRepository(): IAdvertChannelRepository;
  createTelegramAccountRepository(): ITelegramAccountRepository;
  createSendingScheduleRepository(): ISendingScheduleRepository;
  createScheduledDeliveryRepository(): IScheduledDeliveryRepository;
  createLeadRepository(): ILeadRepository;
}

interface UseCaseFactory {
  // Adverts
  createAdvertUseCase(): CreateAdvertUseCase;
  getAdvertUseCase(): GetAdvertUseCase;
  listAdvertsUseCase(): ListAdvertsUseCase;
  updateAdvertUseCase(): UpdateAdvertUseCase;
  deleteAdvertUseCase(): DeleteAdvertUseCase;
  
  // Channels
  createChannelUseCase(): CreateChannelUseCase;
  deleteChannelUseCase(): DeleteChannelUseCase;
  getChannelUseCase(): GetChannelUseCase;
  listAllChannelsUseCase(): ListAllChannelsUseCase;
  listChannelsUseCase(): ListChannelsUseCase;
  countChannelsUseCase(): CountChannelsUseCase;
  listChannelCategoriesUseCase(): ListChannelCategoriesUseCase;
  updateChannelUseCase(): UpdateChannelUseCase;
  
  // Advert-Channels
  getAdvertChannelsUseCase(): GetAdvertChannelsUseCase;
  setAdvertChannelsUseCase(): SetAdvertChannelsUseCase;
  getAdvertTelegramAccountsUseCase(): GetAdvertTelegramAccountsUseCase;
  
  // Telegram Accounts
  createTelegramAccountUseCase(): CreateTelegramAccountUseCase;
  deleteTelegramAccountUseCase(): DeleteTelegramAccountUseCase;
  finishTelegramAccountAuthUseCase(): FinishTelegramAccountAuthUseCase;
  getTelegramAccountUseCase(): GetTelegramAccountUseCase;
  listTelegramAccountsUseCase(): ListTelegramAccountsUseCase;
  startTelegramAccountAuthUseCase(): StartTelegramAccountAuthUseCase;
  updateTelegramAccountUseCase(): UpdateTelegramAccountUseCase;
  getTelegramAccountBrandingUseCase(): GetTelegramAccountBrandingUseCase;
  updateTelegramAccountBrandingUseCase(): UpdateTelegramAccountBrandingUseCase;
  
  // Sending Schedules
  createSendingScheduleUseCase(): CreateSendingScheduleUseCase;
  listSendingSchedulesUseCase(): ListSendingSchedulesUseCase;
  getSendingScheduleUseCase(): GetSendingScheduleUseCase;
  
  // Scheduled Deliveries
  getScheduledDeliveryUseCase(): GetScheduledDeliveryUseCase;
  listScheduledDeliveriesUseCase(): ListScheduledDeliveriesUseCase;

  // Leads
  listLeadsUseCase(): ListLeadsUseCase;
  processLeadUseCase(): ProcessLeadUseCase;

  // Users
  getCurrentUserUseCase(): GetCurrentUserUseCase;

  // Admin
  listAdminChannelsUseCase(): ListAdminChannelsUseCase;
  createAdminChannelUseCase(): CreateAdminChannelUseCase;
  updateAdminChannelUseCase(): UpdateAdminChannelUseCase;
  createChannelCategoryUseCase(): CreateChannelCategoryUseCase;
  renameChannelCategoryUseCase(): RenameChannelCategoryUseCase;
  startChannelImportUseCase(): StartChannelImportUseCase;
  getChannelImportUseCase(): GetChannelImportUseCase;
  listChannelImportsUseCase(): ListChannelImportsUseCase;
  saveChannelImportRowsUseCase(): SaveChannelImportRowsUseCase;
  applyChannelImportUseCase(): ApplyChannelImportUseCase;
  loadDeliveryOverviewUseCase(): LoadDeliveryOverviewUseCase;
  listDeliveryEventsUseCase(): ListDeliveryEventsUseCase;
  getDeliveryEventUseCase(): GetDeliveryEventUseCase;
  listCrawlersUseCase(): ListCrawlersUseCase;
  addCrawlerUseCase(): AddCrawlerUseCase;
  importCrawlerUseCase(): ImportCrawlerUseCase;
  listCrawlerChannelsUseCase(): ListCrawlerChannelsUseCase;
  assignCrawlerChannelsUseCase(): AssignCrawlerChannelsUseCase;
  unassignCrawlerChannelUseCase(): UnassignCrawlerChannelUseCase;
  requestCrawlerCodeUseCase(): RequestCrawlerCodeUseCase;
  confirmCrawlerCodeUseCase(): ConfirmCrawlerCodeUseCase;
  releaseCrawlerUseCase(): ReleaseCrawlerUseCase;
  listTelegramDialogsUseCase(): ListTelegramDialogsUseCase;
  listTelegramDialogMessagesUseCase(): ListTelegramDialogMessagesUseCase;
  sendTelegramMessageUseCase(): SendTelegramMessageUseCase;
  getTelegramInboxSettingsUseCase(): GetTelegramInboxSettingsUseCase;
  updateTelegramInboxSettingsUseCase(): UpdateTelegramInboxSettingsUseCase;
  searchTelegramPeersUseCase(): SearchTelegramPeersUseCase;
  joinTelegramPeerUseCase(): JoinTelegramPeerUseCase;
  pressTelegramInlineButtonUseCase(): PressTelegramInlineButtonUseCase;
  listDiscoveredChannelsUseCase(): ListDiscoveredChannelsUseCase;
  importDiscoveredChannelsUseCase(): ImportDiscoveredChannelsUseCase;
  rejectDiscoveredChannelsUseCase(): RejectDiscoveredChannelsUseCase;
}

// Enhanced DI Container with factory pattern
class DIContainer {
  private singletons = new Map<string, any>();
  private factories = new Map<string, () => any>();

  // Register singleton factory
  registerSingleton<T>(token: string, factory: () => T): void {
    this.factories.set(token, factory);
  }

  // Register transient factory
  registerTransient<T>(token: string, factory: () => T): void {
    this.factories.set(`transient:${token}`, factory);
  }

  // Resolve singleton
  resolveSingleton<T>(token: string): T {
    if (!this.singletons.has(token)) {
      const factory = this.factories.get(token);
      if (!factory) throw new Error(`No factory registered for token: ${token}`);
      this.singletons.set(token, factory());
    }
    return this.singletons.get(token);
  }

  // Resolve transient (always creates new instance)
  resolveTransient<T>(token: string): T {
    const factory = this.factories.get(`transient:${token}`);
    if (!factory) throw new Error(`No transient factory registered for token: ${token}`);
    return factory();
  }

  // Legacy method for backward compatibility
  resolve<T>(token: string, factory: () => T): T {
    if (!this.singletons.has(token)) this.singletons.set(token, factory());
    return this.singletons.get(token);
  }
}

const container = new DIContainer();

// Repository Factory Implementation
class DefaultRepositoryFactory implements RepositoryFactory {

  createAdvertRepository(): IAdvertRepository {
    return container.resolveSingleton('advertRepository');
  }

  createChannelRepository(): IChannelRepository {
    return container.resolveSingleton('channelRepository');
  }

  createChannelCategoryRepository(): IChannelCategoryRepository {
    return container.resolveSingleton('channelCategoryRepository');
  }

  createAdvertChannelRepository(): IAdvertChannelRepository {
    return container.resolveSingleton('advertChannelRepository');
  }

  createTelegramAccountRepository(): ITelegramAccountRepository {
    return container.resolveSingleton('telegramAccountRepository');
  }

  createSendingScheduleRepository(): ISendingScheduleRepository {
    return container.resolveSingleton('sendingScheduleRepository');
  }

  createScheduledDeliveryRepository(): IScheduledDeliveryRepository {
    return container.resolveSingleton('scheduledDeliveryRepository');
  }

  createLeadRepository(): ILeadRepository {
    return container.resolveSingleton('leadRepository');
  }
}

// Use Case Factory Implementation
class DefaultUseCaseFactory implements UseCaseFactory {
  constructor(private repositoryFactory: RepositoryFactory) {}

  // Adverts
  createAdvertUseCase(): CreateAdvertUseCase {
    return container.resolveSingleton('createAdvertUC');
  }

  getAdvertUseCase(): GetAdvertUseCase {
    return container.resolveSingleton('getAdvertUC');
  }

  listAdvertsUseCase(): ListAdvertsUseCase {
    return container.resolveSingleton('listAdvertsUC');
  }

  updateAdvertUseCase(): UpdateAdvertUseCase {
    return container.resolveSingleton('updateAdvertUC');
  }

  deleteAdvertUseCase(): DeleteAdvertUseCase {
    return container.resolveSingleton('deleteAdvertUC');
  }

  // Channels
  createChannelUseCase(): CreateChannelUseCase {
    return container.resolveSingleton('createChannelUC');
  }

  deleteChannelUseCase(): DeleteChannelUseCase {
    return container.resolveSingleton('deleteChannelUC');
  }

  getChannelUseCase(): GetChannelUseCase {
    return container.resolveSingleton('getChannelUC');
  }

  listAllChannelsUseCase(): ListAllChannelsUseCase {
    return container.resolveSingleton('listAllChannelsUC');
  }

  listChannelsUseCase(): ListChannelsUseCase {
    return container.resolveSingleton('listChannelsUC');
  }

  countChannelsUseCase(): CountChannelsUseCase {
    return container.resolveSingleton('countChannelsUC');
  }

  listChannelCategoriesUseCase(): ListChannelCategoriesUseCase {
    return container.resolveSingleton('listChannelCategoriesUC');
  }

  updateChannelUseCase(): UpdateChannelUseCase {
    return container.resolveSingleton('updateChannelUC');
  }

  // Advert-Channels
  getAdvertChannelsUseCase(): GetAdvertChannelsUseCase {
    return container.resolveSingleton('getAdvertChannelsUC');
  }

  setAdvertChannelsUseCase(): SetAdvertChannelsUseCase {
    return container.resolveSingleton('setAdvertChannelsUC');
  }

  getAdvertTelegramAccountsUseCase(): GetAdvertTelegramAccountsUseCase {
    return container.resolveSingleton('getAdvertTelegramAccountsUC');
  }

  // Telegram Accounts
  createTelegramAccountUseCase(): CreateTelegramAccountUseCase {
    return container.resolveSingleton('createTelegramAccountUC');
  }

  deleteTelegramAccountUseCase(): DeleteTelegramAccountUseCase {
    return container.resolveSingleton('deleteTelegramAccountUC');
  }

  finishTelegramAccountAuthUseCase(): FinishTelegramAccountAuthUseCase {
    return container.resolveSingleton('finishTelegramAccountAuthUC');
  }

  getTelegramAccountUseCase(): GetTelegramAccountUseCase {
    return container.resolveSingleton('getTelegramAccountUC');
  }

  listTelegramAccountsUseCase(): ListTelegramAccountsUseCase {
    return container.resolveSingleton('listTelegramAccountsUC');
  }

  startTelegramAccountAuthUseCase(): StartTelegramAccountAuthUseCase {
    return container.resolveSingleton('startTelegramAccountAuthUC');
  }

  updateTelegramAccountUseCase(): UpdateTelegramAccountUseCase {
    return container.resolveSingleton('updateTelegramAccountUC');
  }

  getTelegramAccountBrandingUseCase(): GetTelegramAccountBrandingUseCase {
    return container.resolveSingleton('getTelegramAccountBrandingUC');
  }

  updateTelegramAccountBrandingUseCase(): UpdateTelegramAccountBrandingUseCase {
    return container.resolveSingleton('updateTelegramAccountBrandingUC');
  }

  // Sending Schedules
  createSendingScheduleUseCase(): CreateSendingScheduleUseCase {
    return container.resolveSingleton('createSendingScheduleUC');
  }

  listSendingSchedulesUseCase(): ListSendingSchedulesUseCase {
    return container.resolveSingleton('listSendingSchedulesUC');
  }

  getSendingScheduleUseCase(): GetSendingScheduleUseCase {
    return container.resolveSingleton('getSendingScheduleUC');
  }

  // Scheduled Deliveries
  getScheduledDeliveryUseCase(): GetScheduledDeliveryUseCase {
    return container.resolveSingleton('getScheduledDeliveryUC');
  }

  listScheduledDeliveriesUseCase(): ListScheduledDeliveriesUseCase {
    return container.resolveSingleton('listScheduledDeliveriesUC');
  }

  listLeadsUseCase(): ListLeadsUseCase {
    return container.resolveSingleton('listLeadsUC');
  }

  processLeadUseCase(): ProcessLeadUseCase {
    return container.resolveSingleton('processLeadUC');
  }

  getCurrentUserUseCase(): GetCurrentUserUseCase {
    return container.resolveSingleton('getCurrentUserUC');
  }

  listAdminChannelsUseCase(): ListAdminChannelsUseCase {
    return container.resolveSingleton('listAdminChannelsUC');
  }

  createAdminChannelUseCase(): CreateAdminChannelUseCase {
    return container.resolveSingleton('createAdminChannelUC');
  }

  updateAdminChannelUseCase(): UpdateAdminChannelUseCase {
    return container.resolveSingleton('updateAdminChannelUC');
  }

  createChannelCategoryUseCase(): CreateChannelCategoryUseCase {
    return container.resolveSingleton('createChannelCategoryUC');
  }

  renameChannelCategoryUseCase(): RenameChannelCategoryUseCase {
    return container.resolveSingleton('renameChannelCategoryUC');
  }

  startChannelImportUseCase(): StartChannelImportUseCase {
    return container.resolveSingleton('startChannelImportUC');
  }

  getChannelImportUseCase(): GetChannelImportUseCase {
    return container.resolveSingleton('getChannelImportUC');
  }

  listChannelImportsUseCase(): ListChannelImportsUseCase {
    return container.resolveSingleton('listChannelImportsUC');
  }

  saveChannelImportRowsUseCase(): SaveChannelImportRowsUseCase {
    return container.resolveSingleton('saveChannelImportRowsUC');
  }

  applyChannelImportUseCase(): ApplyChannelImportUseCase {
    return container.resolveSingleton('applyChannelImportUC');
  }

  loadDeliveryOverviewUseCase(): LoadDeliveryOverviewUseCase {
    return container.resolveSingleton('loadDeliveryOverviewUC');
  }

  listDeliveryEventsUseCase(): ListDeliveryEventsUseCase {
    return container.resolveSingleton('listDeliveryEventsUC');
  }

  getDeliveryEventUseCase(): GetDeliveryEventUseCase {
    return container.resolveSingleton('getDeliveryEventUC');
  }

  listCrawlersUseCase(): ListCrawlersUseCase {
    return container.resolveSingleton('listCrawlersUC');
  }

  addCrawlerUseCase(): AddCrawlerUseCase {
    return container.resolveSingleton('addCrawlerUC');
  }

  importCrawlerUseCase(): ImportCrawlerUseCase {
    return container.resolveSingleton('importCrawlerUC');
  }

  listCrawlerChannelsUseCase(): ListCrawlerChannelsUseCase {
    return container.resolveSingleton('listCrawlerChannelsUC');
  }

  assignCrawlerChannelsUseCase(): AssignCrawlerChannelsUseCase {
    return container.resolveSingleton('assignCrawlerChannelsUC');
  }

  unassignCrawlerChannelUseCase(): UnassignCrawlerChannelUseCase {
    return container.resolveSingleton('unassignCrawlerChannelUC');
  }

  requestCrawlerCodeUseCase(): RequestCrawlerCodeUseCase {
    return container.resolveSingleton('requestCrawlerCodeUC');
  }

  confirmCrawlerCodeUseCase(): ConfirmCrawlerCodeUseCase {
    return container.resolveSingleton('confirmCrawlerCodeUC');
  }

  releaseCrawlerUseCase(): ReleaseCrawlerUseCase {
    return container.resolveSingleton('releaseCrawlerUC');
  }

  listTelegramDialogsUseCase(): ListTelegramDialogsUseCase {
    return container.resolveSingleton('listTelegramDialogsUC');
  }

  listTelegramDialogMessagesUseCase(): ListTelegramDialogMessagesUseCase {
    return container.resolveSingleton('listTelegramDialogMessagesUC');
  }

  sendTelegramMessageUseCase(): SendTelegramMessageUseCase {
    return container.resolveSingleton('sendTelegramMessageUC');
  }

  getTelegramInboxSettingsUseCase(): GetTelegramInboxSettingsUseCase {
    return container.resolveSingleton('getTelegramInboxSettingsUC');
  }

  updateTelegramInboxSettingsUseCase(): UpdateTelegramInboxSettingsUseCase {
    return container.resolveSingleton('updateTelegramInboxSettingsUC');
  }

  searchTelegramPeersUseCase(): SearchTelegramPeersUseCase {
    return container.resolveSingleton('searchTelegramPeersUC');
  }

  joinTelegramPeerUseCase(): JoinTelegramPeerUseCase {
    return container.resolveSingleton('joinTelegramPeerUC');
  }

  pressTelegramInlineButtonUseCase(): PressTelegramInlineButtonUseCase {
    return container.resolveSingleton('pressTelegramInlineButtonUC');
  }

  listDiscoveredChannelsUseCase(): ListDiscoveredChannelsUseCase {
    return container.resolveSingleton('listDiscoveredChannelsUC');
  }

  importDiscoveredChannelsUseCase(): ImportDiscoveredChannelsUseCase {
    return container.resolveSingleton('importDiscoveredChannelsUC');
  }

  rejectDiscoveredChannelsUseCase(): RejectDiscoveredChannelsUseCase {
    return container.resolveSingleton('rejectDiscoveredChannelsUC');
  }
}

// Register all dependencies
function setupContainer(): void {
  const authTokenProvider = new FirebaseAuthTokenProvider();

  // Register Repositories
  container.registerSingleton('advertRepository', () => new AxiosAdvertRepository(authTokenProvider));
  container.registerSingleton('channelRepository', () => new ChannelsAxiosRepository(authTokenProvider));
  container.registerSingleton('channelCategoryRepository', () => new ChannelCategoriesAxiosRepository(authTokenProvider));
  container.registerSingleton('advertChannelRepository', () => new AdvertChannelsAxiosRepository(authTokenProvider));
  container.registerSingleton('telegramAccountRepository', () => new TelegramAccountsAxiosRepository(authTokenProvider));
  container.registerSingleton('sendingScheduleRepository', () => new SendingSchedulesAxiosRepository(authTokenProvider));
  container.registerSingleton('scheduledDeliveryRepository', () => new ScheduledDeliveriesAxiosRepository(authTokenProvider));
  container.registerSingleton('leadRepository', () => new LeadsAxiosRepository(authTokenProvider));

  // Register Advert Use Cases
  container.registerSingleton('createAdvertUC', () => new CreateAdvertUseCase(container.resolveSingleton('advertRepository')));
  container.registerSingleton('getAdvertUC', () => new GetAdvertUseCase(container.resolveSingleton('advertRepository')));
  container.registerSingleton('listAdvertsUC', () => new ListAdvertsUseCase(container.resolveSingleton('advertRepository')));
  container.registerSingleton('updateAdvertUC', () => new UpdateAdvertUseCase(container.resolveSingleton('advertRepository')));
  container.registerSingleton('deleteAdvertUC', () => new DeleteAdvertUseCase(container.resolveSingleton('advertRepository')));

  // Register Channel Use Cases
  container.registerSingleton('createChannelUC', () => new CreateChannelUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('deleteChannelUC', () => new DeleteChannelUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('getChannelUC', () => new GetChannelUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('listAllChannelsUC', () => new ListAllChannelsUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('listChannelsUC', () => new ListChannelsUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('countChannelsUC', () => new CountChannelsUseCase(container.resolveSingleton('channelRepository')));
  container.registerSingleton('listChannelCategoriesUC', () => new ListChannelCategoriesUseCase(container.resolveSingleton('channelCategoryRepository')));
  container.registerSingleton('updateChannelUC', () => new UpdateChannelUseCase(container.resolveSingleton('channelRepository')));

  // Register Advert-Channel Use Cases
  container.registerSingleton('getAdvertChannelsUC', () => new GetAdvertChannelsUseCase(container.resolveSingleton('advertChannelRepository')));
  container.registerSingleton('setAdvertChannelsUC', () => new SetAdvertChannelsUseCase(container.resolveSingleton('advertChannelRepository')));
  container.registerSingleton('getAdvertTelegramAccountsUC', () => new GetAdvertTelegramAccountsUseCase(container.resolveSingleton('advertChannelRepository')));

  // Register Telegram Use Cases
  container.registerSingleton('createTelegramAccountUC', () => new CreateTelegramAccountUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('deleteTelegramAccountUC', () => new DeleteTelegramAccountUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('finishTelegramAccountAuthUC', () => new FinishTelegramAccountAuthUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('getTelegramAccountUC', () => new GetTelegramAccountUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('listTelegramAccountsUC', () => new ListTelegramAccountsUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('startTelegramAccountAuthUC', () => new StartTelegramAccountAuthUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('updateTelegramAccountUC', () => new UpdateTelegramAccountUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('getTelegramAccountBrandingUC', () => new GetTelegramAccountBrandingUseCase(container.resolveSingleton('telegramAccountRepository')));
  container.registerSingleton('updateTelegramAccountBrandingUC', () => new UpdateTelegramAccountBrandingUseCase(container.resolveSingleton('telegramAccountRepository')));

  // Register Sending Schedule Use Cases
  container.registerSingleton('createSendingScheduleUC', () => new CreateSendingScheduleUseCase(container.resolveSingleton('sendingScheduleRepository')));
  container.registerSingleton('listSendingSchedulesUC', () => new ListSendingSchedulesUseCase(container.resolveSingleton('sendingScheduleRepository')));
  container.registerSingleton('getSendingScheduleUC', () => new GetSendingScheduleUseCase(container.resolveSingleton('sendingScheduleRepository')));

  // Register Scheduled Delivery Use Cases
  container.registerSingleton('getScheduledDeliveryUC', () => new GetScheduledDeliveryUseCase(container.resolveSingleton('scheduledDeliveryRepository')));
  container.registerSingleton('listScheduledDeliveriesUC', () => new ListScheduledDeliveriesUseCase(container.resolveSingleton('scheduledDeliveryRepository')));

  // Register Lead Use Cases
  container.registerSingleton('listLeadsUC', () => new ListLeadsUseCase(container.resolveSingleton('leadRepository')));
  container.registerSingleton('processLeadUC', () => new ProcessLeadUseCase(container.resolveSingleton('leadRepository')));

  // Register User Use Cases
  container.registerSingleton('currentUserRepository', () => new CurrentUserAxiosRepository(authTokenProvider));
  container.registerSingleton('getCurrentUserUC', () => new GetCurrentUserUseCase(container.resolveSingleton('currentUserRepository')));

  // Register Admin Use Cases
  container.registerSingleton('adminChannelRepository', () => new AdminChannelsAxiosRepository(authTokenProvider));
  container.registerSingleton('listAdminChannelsUC', () => new ListAdminChannelsUseCase(container.resolveSingleton('adminChannelRepository')));
  container.registerSingleton('createAdminChannelUC', () => new CreateAdminChannelUseCase(container.resolveSingleton('adminChannelRepository')));
  container.registerSingleton('updateAdminChannelUC', () => new UpdateAdminChannelUseCase(container.resolveSingleton('adminChannelRepository')));
  container.registerSingleton('createChannelCategoryUC', () => new CreateChannelCategoryUseCase(container.resolveSingleton('adminChannelRepository')));
  container.registerSingleton('renameChannelCategoryUC', () => new RenameChannelCategoryUseCase(container.resolveSingleton('adminChannelRepository')));
  container.registerSingleton('channelImportRepository', () => new ChannelImportsAxiosRepository(authTokenProvider));
  container.registerSingleton('startChannelImportUC', () => new StartChannelImportUseCase(container.resolveSingleton('channelImportRepository')));
  container.registerSingleton('getChannelImportUC', () => new GetChannelImportUseCase(container.resolveSingleton('channelImportRepository')));
  container.registerSingleton('listChannelImportsUC', () => new ListChannelImportsUseCase(container.resolveSingleton('channelImportRepository')));
  container.registerSingleton('saveChannelImportRowsUC', () => new SaveChannelImportRowsUseCase(container.resolveSingleton('channelImportRepository')));
  container.registerSingleton('applyChannelImportUC', () => new ApplyChannelImportUseCase(container.resolveSingleton('channelImportRepository')));
  container.registerSingleton('deliveryDashboardRepository', () => new DeliveryDashboardAxiosRepository(authTokenProvider));
  container.registerSingleton('loadDeliveryOverviewUC', () => new LoadDeliveryOverviewUseCase(container.resolveSingleton('deliveryDashboardRepository')));
  container.registerSingleton('listDeliveryEventsUC', () => new ListDeliveryEventsUseCase(container.resolveSingleton('deliveryDashboardRepository')));
  container.registerSingleton('getDeliveryEventUC', () => new GetDeliveryEventUseCase(container.resolveSingleton('deliveryDashboardRepository')));
  container.registerSingleton('crawlerRepository', () => new CrawlersAxiosRepository(authTokenProvider));
  container.registerSingleton('listCrawlersUC', () => new ListCrawlersUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('addCrawlerUC', () => new AddCrawlerUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('importCrawlerUC', () => new ImportCrawlerUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('listCrawlerChannelsUC', () => new ListCrawlerChannelsUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('assignCrawlerChannelsUC', () => new AssignCrawlerChannelsUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('unassignCrawlerChannelUC', () => new UnassignCrawlerChannelUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('requestCrawlerCodeUC', () => new RequestCrawlerCodeUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('confirmCrawlerCodeUC', () => new ConfirmCrawlerCodeUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('releaseCrawlerUC', () => new ReleaseCrawlerUseCase(container.resolveSingleton('crawlerRepository')));
  container.registerSingleton('telegramMessengerRepository', () => new TelegramMessengerAxiosRepository(authTokenProvider));
  container.registerSingleton('listTelegramDialogsUC', () => new ListTelegramDialogsUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('listTelegramDialogMessagesUC', () => new ListTelegramDialogMessagesUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('sendTelegramMessageUC', () => new SendTelegramMessageUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('getTelegramInboxSettingsUC', () => new GetTelegramInboxSettingsUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('updateTelegramInboxSettingsUC', () => new UpdateTelegramInboxSettingsUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('searchTelegramPeersUC', () => new SearchTelegramPeersUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('joinTelegramPeerUC', () => new JoinTelegramPeerUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('pressTelegramInlineButtonUC', () => new PressTelegramInlineButtonUseCase(container.resolveSingleton('telegramMessengerRepository')));
  container.registerSingleton('discoveredChannelRepository', () => new DiscoveredChannelsAxiosRepository(authTokenProvider));
  container.registerSingleton('listDiscoveredChannelsUC', () => new ListDiscoveredChannelsUseCase(container.resolveSingleton('discoveredChannelRepository')));
  container.registerSingleton('importDiscoveredChannelsUC', () => new ImportDiscoveredChannelsUseCase(container.resolveSingleton('discoveredChannelRepository')));
  container.registerSingleton('rejectDiscoveredChannelsUC', () => new RejectDiscoveredChannelsUseCase(container.resolveSingleton('discoveredChannelRepository')));
}

// Initialize container
setupContainer();

// Create factory instances
const repositoryFactory = new DefaultRepositoryFactory();
const useCaseFactory = new DefaultUseCaseFactory(repositoryFactory);

// Legacy export for backward compatibility
export const useCases = {
  createAdvert: () => useCaseFactory.createAdvertUseCase(),
  getAdvert: () => useCaseFactory.getAdvertUseCase(),
  listAdverts: () => useCaseFactory.listAdvertsUseCase(),
  updateAdvert: () => useCaseFactory.updateAdvertUseCase(),
  deleteAdvert: () => useCaseFactory.deleteAdvertUseCase(),
  
  // Channels
  createChannel: () => useCaseFactory.createChannelUseCase(),
  deleteChannel: () => useCaseFactory.deleteChannelUseCase(),
  getChannel: () => useCaseFactory.getChannelUseCase(),
  listAllChannels: () => useCaseFactory.listAllChannelsUseCase(),
  listChannels: () => useCaseFactory.listChannelsUseCase(),
  countChannels: () => useCaseFactory.countChannelsUseCase(),
  listChannelCategories: () => useCaseFactory.listChannelCategoriesUseCase(),
  updateChannel: () => useCaseFactory.updateChannelUseCase(),
  
  // Advert-Channels
  getAdvertChannels: () => useCaseFactory.getAdvertChannelsUseCase(),
  setAdvertChannels: () => useCaseFactory.setAdvertChannelsUseCase(),
  getAdvertTelegramAccounts: () => useCaseFactory.getAdvertTelegramAccountsUseCase(),
  
  // Telegram Accounts
  createTelegramAccount: () => useCaseFactory.createTelegramAccountUseCase(),
  deleteTelegramAccount: () => useCaseFactory.deleteTelegramAccountUseCase(),
  finishTelegramAccountAuth: () => useCaseFactory.finishTelegramAccountAuthUseCase(),
  getTelegramAccount: () => useCaseFactory.getTelegramAccountUseCase(),
  listTelegramAccounts: () => useCaseFactory.listTelegramAccountsUseCase(),
  startTelegramAccountAuth: () => useCaseFactory.startTelegramAccountAuthUseCase(),
  updateTelegramAccount: () => useCaseFactory.updateTelegramAccountUseCase(),
  getTelegramAccountBranding: () => useCaseFactory.getTelegramAccountBrandingUseCase(),
  updateTelegramAccountBranding: () => useCaseFactory.updateTelegramAccountBrandingUseCase(),
  
  // Sending Schedules
  createSendingSchedule: () => useCaseFactory.createSendingScheduleUseCase(),
  listSendingSchedules: () => useCaseFactory.listSendingSchedulesUseCase(),
  getSendingSchedule: () => useCaseFactory.getSendingScheduleUseCase(),
  
  // Scheduled Deliveries
  getScheduledDelivery: () => useCaseFactory.getScheduledDeliveryUseCase(),
  listScheduledDeliveries: () => useCaseFactory.listScheduledDeliveriesUseCase(),

  // Leads
  listLeads: () => useCaseFactory.listLeadsUseCase(),
  processLead: () => useCaseFactory.processLeadUseCase(),

  // Users
  getCurrentUser: () => useCaseFactory.getCurrentUserUseCase(),

  // Admin
  listAdminChannels: () => useCaseFactory.listAdminChannelsUseCase(),
  createAdminChannel: () => useCaseFactory.createAdminChannelUseCase(),
  updateAdminChannel: () => useCaseFactory.updateAdminChannelUseCase(),
  createChannelCategory: () => useCaseFactory.createChannelCategoryUseCase(),
  renameChannelCategory: () => useCaseFactory.renameChannelCategoryUseCase(),
  startChannelImport: () => useCaseFactory.startChannelImportUseCase(),
  getChannelImport: () => useCaseFactory.getChannelImportUseCase(),
  listChannelImports: () => useCaseFactory.listChannelImportsUseCase(),
  saveChannelImportRows: () => useCaseFactory.saveChannelImportRowsUseCase(),
  applyChannelImport: () => useCaseFactory.applyChannelImportUseCase(),
  loadDeliveryOverview: () => useCaseFactory.loadDeliveryOverviewUseCase(),
  listDeliveryEvents: () => useCaseFactory.listDeliveryEventsUseCase(),
  getDeliveryEvent: () => useCaseFactory.getDeliveryEventUseCase(),
  listCrawlers: () => useCaseFactory.listCrawlersUseCase(),
  addCrawler: () => useCaseFactory.addCrawlerUseCase(),
  importCrawler: () => useCaseFactory.importCrawlerUseCase(),
  listCrawlerChannels: () => useCaseFactory.listCrawlerChannelsUseCase(),
  assignCrawlerChannels: () => useCaseFactory.assignCrawlerChannelsUseCase(),
  unassignCrawlerChannel: () => useCaseFactory.unassignCrawlerChannelUseCase(),
  requestCrawlerCode: () => useCaseFactory.requestCrawlerCodeUseCase(),
  confirmCrawlerCode: () => useCaseFactory.confirmCrawlerCodeUseCase(),
  releaseCrawler: () => useCaseFactory.releaseCrawlerUseCase(),
  listTelegramDialogs: () => useCaseFactory.listTelegramDialogsUseCase(),
  listTelegramDialogMessages: () => useCaseFactory.listTelegramDialogMessagesUseCase(),
  sendTelegramMessage: () => useCaseFactory.sendTelegramMessageUseCase(),
  getTelegramInboxSettings: () => useCaseFactory.getTelegramInboxSettingsUseCase(),
  updateTelegramInboxSettings: () => useCaseFactory.updateTelegramInboxSettingsUseCase(),
  searchTelegramPeers: () => useCaseFactory.searchTelegramPeersUseCase(),
  joinTelegramPeer: () => useCaseFactory.joinTelegramPeerUseCase(),
  pressTelegramInlineButton: () => useCaseFactory.pressTelegramInlineButtonUseCase(),
  listDiscoveredChannels: () => useCaseFactory.listDiscoveredChannelsUseCase(),
  importDiscoveredChannels: () => useCaseFactory.importDiscoveredChannelsUseCase(),
  rejectDiscoveredChannels: () => useCaseFactory.rejectDiscoveredChannelsUseCase(),
};

export type UseCases = typeof useCases;

// Export factories for advanced use cases
export { container, repositoryFactory, useCaseFactory };
export type { RepositoryFactory, UseCaseFactory };
